<script lang="ts">
import { computed, defineComponent, inject, PropType, ref } from "vue";
import Steps from "@/components/Steps.vue";
import Modal from "@/components/Modal.vue";
import Abschluss from "@/components/abschluss/Abschluss.vue";
import formatUtils from "@/businesslogic/utils/formatUtils";
import Zeile from "@/components/grid/Zeile.vue";
import TitelZelle from "@/components/grid/TitelZelle.vue";
import Zelle from "@/components/grid/Zelle.vue";
import { NavvItem } from "@/model/NavItem";
import IconSprite from "@/components/IconSprite.vue";
import dataUtils from "@/businesslogic/utils/dataUtils";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "AbschlussVHV2024",
  emits: ["prev-page", "next-page", "goto-page", "handle-error"],
  components: {
    Zeile,
    TitelZelle,
    Zelle,
    Modal,
    Abschluss,
    Steps,
    IconSprite,
  },
  props: {
    navItems: Object as PropType<NavvItem[]>,
  },
  setup(props, { emit }) {
    const piniaStore = useFormStore();
    const istSichtbar: any = inject("istSichtbar");
    const wechsleSichtbarkeit: any = inject("wechsleSichtbarkeit");

    const fehler = ref({} as any);

    const { formatCurrency } = formatUtils();
    const { gte } = dataUtils();

    const listeDerZusatzmodule = computed(() => {
      let module = "";
      if (piniaStore.state.tarifinformationen.fahrradschutz) {
        module += "Fahrraddiebstahl, ";
      }
      if (piniaStore.state.tarifinformationen.glasschutz) {
        module += "Glasschutz, ";
      }
      if (piniaStore.state.tarifinformationen.elementar) {
        module += "Naturgewalten (Elementar), ";
      }
      if (piniaStore.state.tarifinformationen.proklima) {
        module += "Pro Klima, ";
      }
      if (piniaStore.state.tarifinformationen.soforthilfe) {
        module += "Soforthilfe, ";
      }
      return module.slice(0, module.length - 2);
    });

    const gebaeudetypText = computed(() => {
      if (gte(piniaStore.state.gebaeudedaten.wohnflaeche, 80)) {
        switch (piniaStore.state.gebaeudedaten.gebaeudetyp) {
          case "001":
            return ", Einfamilienhaus";
          case "002":
            return ", Zwei-/Mehrfamilienhaus";
        }
      }
      return "";
    });

    function geheZuVergleich() {
      emit("goto-page", { pageIndex: 1 });
    }

    return {
      props,
      piniaStore,
      fehler,
      formatCurrency,
      wechsleSichtbarkeit,
      istSichtbar,
      listeDerZusatzmodule,
      geheZuVergleich,
      gebaeudetypText,
    };
  },
});
</script>

<template>
  <steps v-bind:items="navItems" :active="3" @goto-page="$emit('goto-page', $event)" />

  <abschluss
    produkt="vhv2024"
    :zeige-versichertes-objekt="true"
    :zeige-tarif="true"
    :zeige-angaben-bestaetigen="!piniaStore.state.isMakler"
    @next-page="$emit('next-page', $event)"
    @goto-page="$emit('goto-page', $event)"
  >
    <template v-slot:versichertes-objekt>
      <strong
        >Versicherungssumme: {{ formatCurrency(piniaStore.state.tarifinformationen.versicherungssumme, 0) }}</strong
      ><br />
      Wohnfläche: {{ piniaStore.state.gebaeudedaten.wohnflaeche }} m2, Wohnort:
      {{ piniaStore.state.personendaten.versicherungsnehmer.adresse.plz }}
      {{ piniaStore.state.personendaten.versicherungsnehmer.adresse.ort }},
      {{ piniaStore.state.personendaten.versicherungsnehmer.adresse.strasse }}
      {{ piniaStore.state.personendaten.versicherungsnehmer.adresse.hausnummer }}
      {{ gebaeudetypText }}
    </template>

    <template v-slot:zusatzmodule v-if="listeDerZusatzmodule"><br />{{ listeDerZusatzmodule }}</template>
    <template v-if="piniaStore.state.tarifinformationen.extraSelbstbeteiligung" v-slot:zusaetzliche-selbstbeteiligung>
      <zeile class="u-mar-t-2">
        <titel-zelle :breite="3" class="o-grid__cell--4" :ausrichtung-responsiv="false" :vertikal-zentrieren="false">
          Zusätz&shy;liche Selbst&shy;be&shy;teili&shy;gung je Scha&shy;den&nbsp;
          <a class="u-copy-s field-edit" @click="geheZuVergleich">bearbeiten</a>
        </titel-zelle>
        <zelle :breite="9" class="o-grid__cell--8" data-cy="zusaetzlicheSb">
          <strong>250 €</strong> aufgrund fehlender Vorversicherung
        </zelle>
      </zeile>
    </template>
    <template v-if="!piniaStore.state.isMakler" v-slot:angaben-bestaetigen>
      Ja, der Antrag wurde gemäß meinen Angaben erstellt. Ich bestätige, dass mein Haus/meine Wohnung ständig bewohnt
      ist, dass es sich um ein Massiv- oder Fertighaus mit hartem Dach handelt und dass die
      <a @click.stop.prevent="wechsleSichtbarkeit('mindestsicherungen')"> tariflichen Mindestsicherungen </a>
      erfüllt sind.Auf eine Beratung habe ich verzichtet und damit auf den Schadenersatzanspruch nach §6. Abs. 5 VVG.
    </template>
    <!--
      Hinweis: Im Gegensatz zum Direktvertrieb, zeigt Maklervertrieb nur
      bei einigen Produkten eine zweite Schlusserklärung an. Daher muss
      leider im Bedarfsfall die Checkbox bei Bedarf über den TemplateSlot
      mitgegeben werden. Der Code wird im Folgenden dadurch unschön aufgebläht,
      aber eine andere/bessere Lösung fällt mir gerade nicht ein.
      (SPA, 20.07.2021)
    -->
    <template v-if="piniaStore.state.isMakler" v-slot:schlusserklaerungMakler2>
      <input
        type="checkbox"
        class="c-form-control-checkbox__input"
        id="schlusserklaerungMakler2"
        name="schlusserklaerungMakler2"
        required
        aria-required="true"
        aria-label="Ja, der Antrag wurde gemäß meinen Angaben erstellt."
        data-fehlermeldung="Bitte bestätigen Sie die Erklärung."
        data-cy="schlusserklaerungMakler2"
      />
      <label class="c-form-control-checkbox__label" for="schlusserklaerungMakler2">
        <icon-sprite icon-name="icon-check-mark" css-klassen="c-form-control-checkbox__icon" :ist-klein="true" />
        <span class="c-form-control-checkbox__label-text">
          Ja, der Antrag wurde gemäß meinen Angaben erstellt. Der/die Versicherungsmakler/in bestätigt stellvertretend
          im Auftrage seines Mandanten, dass mein Haus/meine Wohnung ständig bewohnt ist und dass die tariflichen
          Mindestsicherungen erfüllt sind.
        </span>
      </label>
    </template>
  </abschluss>

  <modal
    id="mindestsicherungen"
    v-if="istSichtbar('mindestsicherungen')"
    titel="Tarifliche Mindestsicherungen"
    text="Unter einer Mindestsicherung der Wohnung versteht die GEV die Sicherung von Haustüren bzw. Wohnungsabschlusstüren durch ein Zylinderschloss mit Sicherheitsbeschlag (von außen nicht abschraubbar und über bündig montierte – max. 5 mm überstehende – Schließzylinder). Gleiches gilt für die Sicherung von weiteren Außentüren (z.B. Terrassen-, Kellertüren). Trifft das nicht zu, kontaktieren Sie uns bitte unter 040 3766 3366."
    @closeModal="wechsleSichtbarkeit('mindestsicherungen')"
  />
</template>
